/**
 * ExamAccessDashboard — per-exam admin panel showing every enrolled student's
 * access state + bulk activate/restrict + reset-attempt + special-access.
 *
 * Pulls from GET /api/school/tenants/{tid}/exams/{eid}/access which already
 * computes: access_status, session_status, fee_waiver/fee_gated flags,
 * special_access_until, previous_attempt_id.
 *
 * Mounted inside the SchoolExams detail view (next to the notifications
 * audit panel).
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Search, Shield, ShieldOff, RotateCcw, Clock4, CheckSquare, AlertTriangle, Lock, WalletMinimal } from 'lucide-react';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';

export default function ExamAccessDashboard({ tenantId, examId }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [restrictDlg, setRestrictDlg] = useState(false);
  const [restrictReason, setRestrictReason] = useState('');
  const [resetDlg, setResetDlg] = useState(null);  // {student_id, name}
  const [specialDlg, setSpecialDlg] = useState(null);
  const [overrideFeeDlg, setOverrideFeeDlg] = useState(null);  // {id, name}

  const { data, isLoading } = useQuery({
    queryKey: ['exam-access', tenantId, examId],
    queryFn: () => api.get(`/school/tenants/${tenantId}/exams/${examId}/access`).then(r => r.data),
    enabled: !!(tenantId && examId),
  });

  const students = data?.students || [];
  const filtered = useMemo(() => {
    if (!search) return students;
    const q = search.toLowerCase();
    return students.filter(s =>
      (`${s.first_name} ${s.last_name}`).toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q));
  }, [students, search]);

  const eligibleIds = filtered.filter(s => !['restricted', 'locked'].includes(s.access_status)).map(s => s.student_id);
  const allSelected = filtered.length > 0 && filtered.every(s => selected.has(s.student_id));

  const toggleAll = () => {
    const ns = new Set(selected);
    if (allSelected) filtered.forEach(s => ns.delete(s.student_id));
    else filtered.forEach(s => ns.add(s.student_id));
    setSelected(ns);
  };
  const toggleOne = (sid) => {
    const ns = new Set(selected);
    ns.has(sid) ? ns.delete(sid) : ns.add(sid);
    setSelected(ns);
  };

  const refresh = () => qc.invalidateQueries({ queryKey: ['exam-access', tenantId, examId] });

  const activateMut = useMutation({
    mutationFn: (ids) => api.post(`/school/tenants/${tenantId}/exams/${examId}/access/activate`,
      { student_ids: ids, reason: 'Bulk activation' }).then(r => r.data),
    onSuccess: (d) => { toast.success(`Activated ${d.count} students`); setSelected(new Set()); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const restrictMut = useMutation({
    mutationFn: () => api.post(`/school/tenants/${tenantId}/exams/${examId}/access/restrict`,
      { student_ids: Array.from(selected), reason: restrictReason }).then(r => r.data),
    onSuccess: (d) => { toast.success(`Restricted ${d.count} students`); setRestrictDlg(false); setRestrictReason(''); setSelected(new Set()); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const unrestrictMut = useMutation({
    mutationFn: (ids) => api.post(`/school/tenants/${tenantId}/exams/${examId}/access/unrestrict`,
      { student_ids: ids }).then(r => r.data),
    onSuccess: () => { toast.success('Unrestricted'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const statusBadge = (s) => {
    const map = {
      restricted: { c: 'bg-rose-100 text-rose-700 border-rose-200', icon: ShieldOff, label: 'Restricted' },
      locked: { c: 'bg-slate-200 text-slate-700', icon: Lock, label: 'Locked' },
      special_access: { c: 'bg-violet-100 text-violet-700 border-violet-200', icon: Shield, label: 'Special access' },
      activated: { c: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckSquare, label: 'Activated' },
      eligible: { c: 'bg-slate-50 text-slate-600 border-slate-200', icon: CheckSquare, label: 'Eligible' },
    };
    const meta = map[s.access_status] || map.eligible;
    const Icon = meta.icon;
    return <Badge variant="outline" className={`text-[10px] ${meta.c}`}><Icon className="h-3 w-3 mr-0.5" />{meta.label}</Badge>;
  };

  return (
    <Card data-testid="exam-access-dashboard">
      <CardHeader className="pb-3 flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Shield className="h-4 w-4 text-fuchsia-600" /> Access Dashboard
          <Badge variant="outline" className="ml-1 text-[10px]">{students.length} students</Badge>
        </CardTitle>
        <div className="flex items-center gap-1 flex-wrap">
          <Button size="sm" variant="outline" disabled={!eligibleIds.length || activateMut.isPending}
                  onClick={() => activateMut.mutate(eligibleIds)} data-testid="ead-activate-all">
            Activate all eligible
          </Button>
          <Button size="sm" variant="outline" disabled={!selected.size}
                  onClick={() => activateMut.mutate(Array.from(selected))} data-testid="ead-activate-selected">
            Activate selected ({selected.size})
          </Button>
          <Button size="sm" variant="outline" disabled={!selected.size}
                  onClick={() => setRestrictDlg(true)} data-testid="ead-restrict-selected">
            <ShieldOff className="h-3.5 w-3.5 mr-1" /> Restrict ({selected.size})
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative mb-3">
          <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email" className="pl-7 h-9 text-sm" data-testid="ead-search" />
        </div>
        {isLoading ? (
          <p className="text-xs text-slate-400">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6" data-testid="ead-empty">No students match.</p>
        ) : (
          <div className="border rounded overflow-hidden">
            <div className="grid grid-cols-[24px_1fr_120px_120px_120px_140px] gap-2 px-2 py-1.5 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
              <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="ead-select-all" />
              <span>Student</span>
              <span>Access</span>
              <span>Attempt</span>
              <span>Fee</span>
              <span>Actions</span>
            </div>
            <div className="max-h-[420px] overflow-auto divide-y">
              {filtered.map(s => (
                <div key={s.student_id} className="grid grid-cols-[24px_1fr_120px_120px_120px_140px] gap-2 px-2 py-2 text-xs items-center" data-testid={`ead-row-${s.student_id}`}>
                  <Checkbox checked={selected.has(s.student_id)} onCheckedChange={() => toggleOne(s.student_id)} data-testid={`ead-pick-${s.student_id}`} />
                  <div className="min-w-0">
                    <p className="font-medium truncate">{s.first_name} {s.last_name}</p>
                    <p className="text-slate-400 truncate">{s.email}</p>
                  </div>
                  <div>
                    {statusBadge(s)}
                    {s.access_reason && <p className="text-[10px] text-slate-400 truncate mt-0.5">{s.access_reason}</p>}
                  </div>
                  <div className="text-slate-600">{s.session_status || '—'}{s.last_saved_at && <p className="text-[10px] text-slate-400">{s.last_saved_at.slice(11,16)}</p>}</div>
                  <div>{s.fee_gated ? <Badge variant="outline" className="text-[10px] text-orange-600 border-orange-300">Blocked</Badge> : s.fee_waiver ? <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300">Waived</Badge> : <span className="text-slate-300">—</span>}</div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" title="Reset attempt"
                      onClick={() => setResetDlg({ id: s.student_id, name: `${s.first_name} ${s.last_name}` })}
                      data-testid={`ead-reset-${s.student_id}`}>
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" title="Special access"
                      onClick={() => setSpecialDlg({ id: s.student_id, name: `${s.first_name} ${s.last_name}` })}
                      data-testid={`ead-special-${s.student_id}`}>
                      <Clock4 className="h-3.5 w-3.5" />
                    </Button>
                    {['restricted', 'locked'].includes(s.access_status) && (
                      <Button size="icon" variant="ghost" title="Unrestrict"
                        onClick={() => unrestrictMut.mutate([s.student_id])}
                        data-testid={`ead-unrestrict-${s.student_id}`}>
                        <Shield className="h-3.5 w-3.5 text-emerald-600" />
                      </Button>
                    )}
                    {s.fee_gated && (
                      <Button size="icon" variant="ghost" title="Override fee block"
                        onClick={() => setOverrideFeeDlg({ id: s.student_id, name: `${s.first_name} ${s.last_name}` })}
                        data-testid={`ead-override-fee-${s.student_id}`}>
                        <WalletMinimal className="h-3.5 w-3.5 text-orange-600" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      {/* Restrict dialog */}
      <Dialog open={restrictDlg} onOpenChange={setRestrictDlg}>
        <DialogContent data-testid="ead-restrict-dialog">
          <DialogHeader><DialogTitle>Restrict {selected.size} student{selected.size === 1 ? '' : 's'}</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs">Reason (shown to the student)</Label>
            <Textarea value={restrictReason} onChange={(e) => setRestrictReason(e.target.value)} placeholder="e.g. Disciplinary review — contact admin" rows={3} data-testid="ead-restrict-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestrictDlg(false)}>Cancel</Button>
            <Button disabled={!restrictReason.trim() || restrictMut.isPending} onClick={() => restrictMut.mutate()} data-testid="ead-restrict-confirm">Restrict</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ResetDialog ctx={resetDlg} onClose={() => setResetDlg(null)} tenantId={tenantId} examId={examId} onDone={refresh} />
      <SpecialDialog ctx={specialDlg} onClose={() => setSpecialDlg(null)} tenantId={tenantId} examId={examId} onDone={refresh} />
      <OverrideFeeDialog ctx={overrideFeeDlg} onClose={() => setOverrideFeeDlg(null)} tenantId={tenantId} examId={examId} onDone={refresh} />
    </Card>
  );
}

function ResetDialog({ ctx, onClose, tenantId, examId, onDone }) {
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState('archive_new');
  const [extend, setExtend] = useState('');

  const mut = useMutation({
    mutationFn: () => api.post(`/school/tenants/${tenantId}/exams/${examId}/access/reset-attempt`, {
      student_id: ctx.id, reason, mode,
      extend_close_minutes: extend ? Number(extend) : undefined,
    }).then(r => r.data),
    onSuccess: () => { toast.success('Attempt reset'); onClose(); onDone(); setReason(''); setExtend(''); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!ctx) return null;
  return (
    <Dialog open={!!ctx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ead-reset-dialog">
        <DialogHeader><DialogTitle>Reset attempt for {ctx.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="ead-reset-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="archive_new">Archive previous + start fresh</SelectItem>
                <SelectItem value="extra_attempt">Allow extra attempt (keep history)</SelectItem>
                <SelectItem value="reopen">Reopen previous attempt</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Reason (audit log)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="e.g. Network outage during attempt" data-testid="ead-reset-reason" />
          </div>
          <div>
            <Label className="text-xs">Extend close window for this student (minutes, optional)</Label>
            <Input type="number" value={extend} onChange={(e) => setExtend(e.target.value)} placeholder="e.g. 60" data-testid="ead-reset-extend" />
          </div>
          <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-200 flex items-start gap-1">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>This change only affects {ctx.name}. Other students are not impacted.</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={reason.trim().length < 3 || mut.isPending} onClick={() => mut.mutate()} data-testid="ead-reset-confirm">
            Reset attempt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SpecialDialog({ ctx, onClose, tenantId, examId, onDone }) {
  const [form, setForm] = useState({ reason: '', closes_at: '', duration_override_minutes: '' });

  const mut = useMutation({
    mutationFn: () => api.post(`/school/tenants/${tenantId}/exams/${examId}/access/special`, {
      student_id: ctx.id,
      reason: form.reason,
      closes_at: form.closes_at || null,
      duration_override_minutes: form.duration_override_minutes ? Number(form.duration_override_minutes) : null,
    }).then(r => r.data),
    onSuccess: () => { toast.success('Special access granted'); onClose(); onDone(); setForm({ reason: '', closes_at: '', duration_override_minutes: '' }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!ctx) return null;
  return (
    <Dialog open={!!ctx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ead-special-dialog">
        <DialogHeader><DialogTitle>Special access for {ctx.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Reason</Label>
            <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} placeholder="e.g. Approved medical extension" data-testid="ead-special-reason" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Close window override (ISO)</Label>
              <Input value={form.closes_at} onChange={(e) => setForm({ ...form, closes_at: e.target.value })} placeholder="2026-06-25T18:00Z" data-testid="ead-special-closes" />
            </div>
            <div>
              <Label className="text-xs">Duration override (min)</Label>
              <Input type="number" value={form.duration_override_minutes} onChange={(e) => setForm({ ...form, duration_override_minutes: e.target.value })} placeholder="120" data-testid="ead-special-duration" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!form.reason.trim() || mut.isPending} onClick={() => mut.mutate()} data-testid="ead-special-confirm">
            Grant
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function OverrideFeeDialog({ ctx, onClose, tenantId, examId, onDone }) {
  const [reason, setReason] = useState('');

  const mut = useMutation({
    mutationFn: () => api.post(`/school/tenants/${tenantId}/exams/${examId}/access/override-fee`, {
      student_id: ctx.id, reason,
    }).then(r => r.data),
    onSuccess: () => { toast.success('Fee block overridden'); onClose(); onDone(); setReason(''); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!ctx) return null;
  return (
    <Dialog open={!!ctx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ead-override-fee-dialog">
        <DialogHeader><DialogTitle>Override fee block for {ctx.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-200 flex items-start gap-1">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>This student will be permitted to sit THIS exam despite outstanding fees. Their balance with the bursar is unaffected. The override is recorded in the access audit log.</span>
          </div>
          <div>
            <Label className="text-xs">Reason (audit log)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              placeholder="e.g. Approved by bursar — paying in installments"
              data-testid="ead-override-fee-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={reason.trim().length < 3 || mut.isPending}
            onClick={() => mut.mutate()} data-testid="ead-override-fee-confirm">
            Override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
